import { useEffect, useRef, type FC } from 'react'
import toast from 'react-hot-toast'
import { alpha, useTheme } from '@mui/material/styles'
import packageJson from '../../../package.json'
import useTranslate from '@/hooks/use-translate'
import {
  fetchAppMetaJson,
  isRemoteBuildNewerThanBundle,
} from '@/utils/appVersionMeta'

const POLL_INTERVAL_MS = 10 * 60 * 1000

/** Évite de réafficher le toast à chaque F5 si le bundle reste ancien (cache / dev). Réinitialisé quand le bundle rattrape le serveur. */
const SESSION_TOAST_SHOWN_FOR_BUILD_KEY = 'nabu-version-toast-shown-for'

export const NewVersionNotifier: FC = () => {
  const theme = useTheme()
  const { t: tCommon } = useTranslate('common')
  const lastToastRemoteBuildDateRef = useRef<string | null>(null)
  const themeRef = useRef(theme)
  const tCommonRef = useRef(tCommon)
  themeRef.current = theme
  tCommonRef.current = tCommon

  useEffect(() => {
    const bundleBuildDate = packageJson.buildDate
    if (typeof bundleBuildDate !== 'string' || bundleBuildDate.length === 0) {
      return
    }

    const checkAndNotify = () => {
      void fetchAppMetaJson().then((meta) => {
        if (!meta) {
          return
        }
        if (!isRemoteBuildNewerThanBundle(meta.buildDate, bundleBuildDate)) {
          try {
            sessionStorage.removeItem(SESSION_TOAST_SHOWN_FOR_BUILD_KEY)
          } catch {
            /* private mode, etc. */
          }
          return
        }
        if (lastToastRemoteBuildDateRef.current === meta.buildDate) {
          return
        }
        try {
          if (
            sessionStorage.getItem(SESSION_TOAST_SHOWN_FOR_BUILD_KEY) ===
            meta.buildDate
          ) {
            return
          }
        } catch {
          /* ignore */
        }
        lastToastRemoteBuildDateRef.current = meta.buildDate

        try {
          sessionStorage.setItem(
            SESSION_TOAST_SHOWN_FOR_BUILD_KEY,
            meta.buildDate
          )
        } catch {
          /* ignore */
        }

        const themeSnapshot = themeRef.current
        const tCommonSnapshot = tCommonRef.current

        toast.custom(
          (t) => (
            <div
              style={{
                backdropFilter: 'blur(6px)',
                background: alpha(themeSnapshot.palette.neutral[900], 0.8),
                color: themeSnapshot.palette.common.white,
                boxShadow: themeSnapshot.shadows[16],
                borderRadius: themeSnapshot.shape.borderRadius,
                padding: themeSnapshot.spacing(2),
                maxWidth: 520,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: themeSnapshot.spacing(2),
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12,
                  lineHeight: 1.4,
                }}
              >
                {tCommonSnapshot('newVersionAvailableMessage')}
              </span>
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(t.id)
                  window.location.reload()
                }}
                style={{
                  flexShrink: 0,
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: themeSnapshot.shape.borderRadius,
                  padding: `${themeSnapshot.spacing(0.75)} ${themeSnapshot.spacing(1.5)}`,
                  fontWeight: 600,
                  fontSize: 12,
                  background: themeSnapshot.palette.primary.main,
                  color: themeSnapshot.palette.primary.contrastText,
                }}
              >
                {tCommonSnapshot('refreshPageForNewVersion')}
              </button>
            </div>
          ),
          {
            duration: Infinity,
            id: 'new-version-available',
            position: 'bottom-left',
          }
        )
      })
    }

    checkAndNotify()
    const intervalId = window.setInterval(checkAndNotify, POLL_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [])

  return null
}
